import { Box, Button, FlexBox, Icon, Typography, getTokenCssVar, useScreenClass } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { Price } from '@99designs/pricing';
import type { EsProduct } from '../../product/types';

export type ChatCompletionCardProps = {
    onReview: () => void;
    onContinue: () => void;
    canContinue?: boolean;
    esProduct?: EsProduct;
};

/** Shown in the composer's slot once the chat brief is complete. */
export function ChatCompletionCard({
    onReview,
    onContinue,
    canContinue = true,
    esProduct,
}: ChatCompletionCardProps) {
    const screenClass = useScreenClass();
    const isMobile = screenClass === 'xs';

    return (
        <Box
            style={{
                backgroundColor: getTokenCssVar('SwanSemColorBgActive'),
                border: `1px solid ${getTokenCssVar('SwanSemColorBorderStandard')}`,
                borderBottom: 'none',
                borderRadius: `${getTokenCssVar('SwanSemBorderRadiusStandard')} ${getTokenCssVar(
                    'SwanSemBorderRadiusStandard'
                )} 0 0`,
                padding: getTokenCssVar('SwanSemSpace6'),
            }}
        >
            <FlexBox
                flexDirection={isMobile ? 'column' : 'row'}
                alignItems={isMobile ? 'flex-start' : 'center'}
                justifyContent="space-between"
                gap={3}
                mb={5}
            >
                <Typography fontSkin="title-subsection">
                    {__('Review your brief and place your order')}
                </Typography>
                {esProduct && (
                    <FlexBox alignItems="center" gap={2} style={{ flexShrink: 0 }}>
                        <Typography fontSkin="body-standard-bold">{esProduct.name}</Typography>
                        <Typography fontSkin="title-subsection">
                            <Price price={esProduct.price} />
                        </Typography>
                    </FlexBox>
                )}
            </FlexBox>
            <Box as="ol" style={{ margin: 0, paddingLeft: getTokenCssVar('SwanSemSpace5') }}>
                <Typography as="li" fontSkin="body-standard" mb={2}>
                    {__('Review your brief to make sure everything looks right.')}
                </Typography>
                <Typography as="li" fontSkin="body-standard" mb={5}>
                    {__(
                        'Checkout to start your project, and a designer will begin once payment is complete.'
                    )}
                </Typography>
            </Box>
            <FlexBox
                flexDirection={isMobile ? 'column' : 'row'}
                gap={4}
                alignItems={isMobile ? 'stretch' : 'center'}
            >
                <Button
                    skin="primary"
                    iconPosition="left"
                    onClick={onReview}
                    style={isMobile ? { width: '100%' } : undefined}
                >
                    <Icon iconType="invoice" size="small" />
                    {__('Review and checkout', {
                        _context:
                            'Primary button on the chat brief completion card, taking the customer to review their brief and check out.',
                    })}
                </Button>
                {canContinue && (
                    <Button
                        skin="secondary"
                        iconPosition="left"
                        onClick={onContinue}
                        style={isMobile ? { width: '100%' } : undefined}
                    >
                        <Icon iconType="aiSupport" size="small" />
                        {__('Continue chat')}
                    </Button>
                )}
            </FlexBox>
        </Box>
    );
}
